import React from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Trophy, Users, Calendar, Target } from "lucide-react";
import GoogleSignIn from "../components/GoogleSignIn";

const Home: React.FC = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we have OAuth parameters in the URL
  const urlParams = new URLSearchParams(location.search);
  const code = urlParams.get("code");
  const error = urlParams.get("error");

  // Check if user has session data (for non-OAuth flow)
  const hasSessionData = sessionStorage.getItem("userEmail");
  const hasAccessToken = sessionStorage.getItem("access_token");
  const hasIdToken = sessionStorage.getItem("id_token");

  // If user is authenticated or has session data, redirect to dashboard
  // But don't redirect if they're in the middle of profile setup
  // Also don't redirect if we just logged out (no tokens present)
  if (
    (authStatus === "authenticated" ||
      (hasSessionData && hasAccessToken && hasIdToken)) &&
    !location.pathname.includes("/profile-setup")
  ) {
    return <Navigate to="/dashboard" replace state={{ fromOAuth: true }} />;
  }

  // If we have an OAuth code, show processing message
  if (code) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Processing authentication...</p>
        </div>
      </div>
    );
  }

  // If we have an OAuth error, show error message
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">Error: {error}</p>
          <button
            onClick={() => (window.location.href = "/")}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="rounded-2xl bg-primary p-4 shadow-2xl shadow-primary/30">
              <Trophy className="h-12 w-12 text-primary-foreground" />
            </div>
          </div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
            The Booster Box
          </p>
          <h1 className="mb-4 text-4xl font-bold tracking-tight text-white sm:text-5xl">
            SOVA League Hub
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground sm:text-xl">
            Track Magic: The Gathering and Riftbound leagues, events, and
            championship standings in one place.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card
            className="cursor-pointer text-center transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10"
            onClick={() => navigate("/standings")}
          >
            <CardHeader>
              <div className="flex justify-center mb-2">
                <Trophy className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-lg">League Standings</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Real-time standings with points, wins, and tiebreakers
              </CardDescription>
              <div className="mt-2 text-sm text-primary font-medium">
                View Standings →
              </div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-2">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Event Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Schedule and track tournaments across multiple stores
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-2">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Player Profiles</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Detailed player statistics and performance history
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-2">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Championship</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Top 8 players qualify for the city championship
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Authentication Section */}
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Welcome to SOVA League Hub</CardTitle>
              <CardDescription>
                Sign in to access your league standings and event information
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Google OAuth Option */}
                <div>
                  <GoogleSignIn />
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                {/* Traditional Cognito Sign In */}
                <div className="space-y-3">
                  <button
                    onClick={() =>
                      (window.location.href =
                        "https://standardleaguec25e05fb-c25e05fb-dev.auth.us-east-2.amazoncognito.com/login?response_type=code&client_id=68u1oj5ouepjkmbb6h43j55vje&redirect_uri=http%3A%2F%2Flocalhost%3A3000%2Foauth%2Fcallback&scope=email+openid+profile&prompt=select_account&login_hint=&max_age=0")
                    }
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Sign in with Email
                  </button>

                  <p className="text-xs text-center text-muted-foreground">
                    Don't have an account?{" "}
                    <button
                      onClick={() =>
                        (window.location.href =
                          "https://standardleaguec25e05fb-c25e05fb-dev.auth.us-east-2.amazoncognito.com/signup?response_type=code&client_id=68u1oj5ouepjkmbb6h43j55vje&redirect_uri=http%3A%2F%2Flocalhost%3A3000%2Foauth%2Fcallback&scope=email+openid+profile&prompt=select_account&login_hint=&max_age=0")
                      }
                      className="text-primary hover:underline"
                    >
                      Create one here
                    </button>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-muted-foreground">
          <p className="text-sm">
            © 2026 The Booster Box. Built for the 757 gaming community.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
